import registry from './sites.json';

/**
 * Die Anwendung liefert mehr als einen öffentlichen Auftritt aus.
 *
 * Beide liegen im selben Build und im selben Container; welcher gezeigt wird,
 * entscheidet der **Host** der Anfrage — nicht der Pfad und nicht der Build.
 * Damit ist am Tag einer Domain-Umstellung nichts nachzuziehen: Sobald die
 * Domain auf den Container zeigt, ist der zugehörige Auftritt da.
 *
 * Die Liste steht bewusst in `sites.json` und nicht hier: Die Bauskripte
 * (`scripts/routes.js`, `prerender.js`, `generate-sitemap.js`) brauchen
 * dieselben Angaben, laufen aber in Node und können kein TypeScript lesen.
 * Zwei gepflegte Listen wären zwei Listen, die auseinanderlaufen.
 */
export type SiteKey = keyof typeof registry;

export interface SiteDefinition {
  /** Anzeigename, z.B. für Titel-Suffixe. */
  name: string;
  /** Kanonische Adresse ohne Schrägstrich am Ende — Grundlage für Canonical, OG und Sitemap. */
  url: string;
  /** Alle Hosts, unter denen dieser Auftritt ausgeliefert wird. Kleinbuchstaben, ohne Port. */
  hosts: string[];
  /**
   * Unterordner der vorgerenderten Seiten im Build (`/m38`), leer für den
   * Hauptauftritt. nginx wählt darüber die auszuliefernde Datei.
   */
  prefix: string;
  /** Die indexierbaren Routen dieses Auftritts, ohne dynamische Detailseiten. */
  paths: string[];
  /**
   * Umami-Website, unter der dieser Auftritt gezählt wird.
   *
   * Je Auftritt eine eigene: Das Konzept verlangt, dass sich der Radio-Effekt
   * getrennt auswerten lässt — Besucher, Anrufe, abgeschickte
   * Betreuungs-Checks. In einer gemeinsamen Statistik ginge das in den Zahlen
   * der Kundenwebsite unter.
   */
  umamiWebsiteId: string;

  /**
   * Erkennungszeichen: Favicon, Touch-Icons und Manifest.
   *
   * Die Adressen stehen fest in `public/index.html` und sind für beide Auftritte
   * dieselben; ausgetauscht wird nur, worauf sie zeigen (siehe `useSiteIcons`).
   * Bewusst im HTML und nicht über eine Regel im Webserver: Der Dev-Server ist
   * kein nginx, und eine Weiche, die nur in Produktion greift, ist eine Weiche,
   * die man vor dem Live-Gang nie zu sehen bekommt.
   */
  icons: {
    /** Mehrgrößen-ICO für die Adresszeile. */
    ico: string;
    /** Ordner mit `favicon-32.png`, `favicon-64.png`, `apple-touch-icon.png`. */
    dir: string;
    /** Web-App-Manifest dieses Auftritts. */
    manifest: string;
  };

  /**
   * Routen, die vorgerendert, aber **nicht** in die Sitemap aufgenommen werden.
   *
   * Die Danke-Seite ist so ein Fall: Sie braucht eine eigene Adresse, damit sich
   * abgeschickte Anfragen zählen lassen, hat als Suchergebnis aber nichts zu
   * suchen — wer sie über Google beträte, hätte nie ein Formular ausgefüllt.
   */
  unlisted: string[];
}

export const SITES: Record<SiteKey, SiteDefinition> = registry;

/** Der Auftritt, der gilt, wenn der Host keinem anderen zugeordnet ist. */
export const DEFAULT_SITE: SiteKey = 'antczak';

/**
 * Welcher Auftritt gilt gerade?
 *
 * Reihenfolge ist wichtig. `window.__SITE__` steht nur beim Prerendering: Der
 * Browser lädt die Seite dort von 127.0.0.1, der echte Host ist also gar nicht
 * bekannt — `scripts/prerender.js` legt den Wert deshalb vor dem Rendern hin,
 * genauso wie die Seiteninhalte (siehe `SiteContent.tsx`).
 *
 * Ein unbekannter Host ergibt den Hauptauftritt. Das ist der Fall für
 * `optivisit.gsk-it.de` (Mitarbeiterzugang und Vorschau) und für `localhost`.
 * Den zweiten Auftritt bekommt man in der Entwicklung unter
 * `http://m38.localhost:3000` zu sehen — `*.localhost` zeigt in allen aktuellen
 * Browsern von sich aus auf 127.0.0.1, es ist also kein Eintrag in der
 * hosts-Datei nötig.
 */
export function activeSiteKey(): SiteKey {
  const injected =
    typeof window !== 'undefined' ? (window as any).__SITE__ : undefined;
  if (injected && injected in SITES) return injected as SiteKey;

  const host = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : '';
  const found = (Object.keys(SITES) as SiteKey[]).find((key) => SITES[key].hosts.includes(host));
  return found ?? DEFAULT_SITE;
}

/** Definition des gerade geltenden Auftritts. */
export function activeSite(): SiteDefinition {
  return SITES[activeSiteKey()];
}

/** Kanonische Adresse des gerade geltenden Auftritts, z.B. `https://miteinander38.de`. */
export function siteUrl(): string {
  return activeSite().url;
}
